/**
 * The four list pages (piece 13). Each is the List Viewer of Composite and
 * data-display components over one record type, with its own columns and its
 * own route into the record page beside it. 🔨 The columns are declared here
 * rather than in the screen, so the list page, the CSV export and Global
 * Search's See all N results read one definition.
 */
import { t, type StringKey } from '../strings';
import type { ColumnDef } from '../composites/listViewer';
import { RECORD_ROUTES, type PagedRecord } from './recordPages';
import { RECORD_KIND_NAMES, documentName, documentNamePlural } from '../documents/documentNames';
import { BULK_EDIT_FIELDS, type BulkEditField } from './bulkEdit';
import { SITE_TYPE_COLUMN, SITE_TYPE_OPTIONS_PATH, SITE_TYPE_PARAM } from '../records/siteTypes';
import { PAGE_SETTINGS_ENTRIES, type PageSettingsEntry } from './pageSettings';
import { allView, openClosedViews, statusViews, type BuiltInView } from './builtInViews';
import { ALL_VIEW_ID } from '../shell/savedView';

/**
 * 🔒 How + New on this list is answered (piece 13; decisions 0409, 0441; T180).
 * 🔨 A list whose answer is `none` draws no + New at all: a record raised from
 * another document has nothing to open, and drawing one opened an empty modal
 * that could write nothing (found in review, 2026-09-15).
 */
export type ListCreateKind =
  /** The generic field form, in the Quick-Create modal. */
  | 'fields'
  /** The record's own page: a document opened in edit mode on a Draft (decision 0409), or the New Deal page (decision 2510). */
  | 'page'
  /** The Site's combo window, which writes the building, the site, its roles and the first note together. */
  | 'siteCombo'
  /** The revaluation's window, which posts as it is saved (decision 0441). */
  | 'stockRevaluation'
  /** The vendor credit's window; it carries no table of lines (decision 0449). */
  | 'vendorCredit'
  /** The bill of materials' window: a name and a description, the list made attached to no Deal (decision 1092). */
  | 'bom'
  | 'none';

/** 🔒 A link out to another screen drawn at the end of a list's bar, never a view (piece 20): the Lead list's hygiene screen. */
export interface ListBarLink {
  labelKey: StringKey;
  route: string;
}

export interface ListPageSpec {
  recordType: PagedRecord;
  /** What the Compact band and the New button name, such as Companies. */
  label: string;
  singular: string;
  route: string;
  recordRoute: string;
  columns: readonly ColumnDef[];
  /** The first sort, until the viewer's own saved view carries one. */
  defaultSortKey: string;
  /**
   * 🔒 Pins the compact rows at every width (decision 2852; T2059), for a list whose
   * secondary column is prose and would be cut in the table, such as the Items
   * list's names. Absent, the viewer picks Wide or Compact from its measured width
   * alone (decision 0259), as every other list does.
   */
  layout?: 'compact';
  /** What the empty state says to do next; an empty state never says only that it is empty. */
  emptyAction: string;
  /**
   * 🔒 False where a row has no record page to open (decision 1158; T379,
   * P24-9, the owner's rule of 2026-09-27). A row's Open used to change the
   * address and draw Not Found with no request made. Defaults to true, which is
   * every list: the Vendor Credit, its one user, has a page since decision 7580.
   */
  openable?: boolean;
  /** The page's own actions beside + New, each keyed for the string lookup and the API's gate (decision 0341). */
  actions?: readonly ListPageAction[];
  /** 🔒 What + New opens here (T180). It is what decides whether the button is drawn at all. */
  create: ListCreateKind;
  /** 🔒 True where the list carries a whole-width board beside its rows (decision 0206). */
  boardable?: boolean;
  /** Whether the list draws a checkbox on each row, where it offers a change over picked rows (piece 17; decision 3620). */
  bulk?: 'selectRows';
  /** 🔒 The fields Edit selected changes over picked rows (piece 13; decision 0982): declared once in the shared set, and no list names another. */
  bulkEdit?: readonly BulkEditField[];
  /**
   * 🔒 The list's built-in views beside its columns (decision 2880): each a name
   * and what the read already takes. All leads them; a list that names its All
   * in its own words declares it here, and every other list reads All and its label.
   */
  views?: readonly BuiltInView[];
  /** 🔒 Links out to another screen at the end of the list's bar, never a view (piece 20). */
  barLinks?: readonly ListBarLink[];
  /**
   * 🔒 A filter the list's own address carries (decision 0762; T123): the query
   * parameter, and each value it takes with the title the list reads under it.
   * The Payments list is one list read twice, as Payments received and Payments
   * made, and the parameter rides with + New and with the row a person opens.
   */
  urlFilter?: ListUrlFilter;
  /** 🔒 True where the filter row carries the Tags menu by category, then by tag (T1581, screen E); the Deals list alone so far. */
  tagFilter?: boolean;
  /** 🔒 True where the filter row carries the Series menu, a manufacturer's product line ticked in one press (decision 7640; T3125); the Items list alone. */
  seriesFilter?: boolean;
  /**
   * 🔒 True where the filter row carries the Filter button: several conditions narrow the read at once, without a
   * saved view, and the field menu offers the catalog's attribute types (decision 8800; T3276); the Items list alone.
   */
  conditionsFilter?: boolean;
  /**
   * 🔒 A row of segments above the list, All first and then each value the options route answers (decision 10100; T3476):
   * a segment narrows the read through the list's criteria on one column, and the address carries it. The Sites list's
   * site types alone so far.
   */
  segmentFilter?: ListSegmentFilter;
  /**
   * 🔒 The settings that govern this list, which a gear at the right end of its
   * toolbar row opens (decision 2805; T1614). The list's own page draws it; a
   * list standing as a pane beside a record has no toolbar row and draws none.
   */
  settingsEntries?: readonly PageSettingsEntry[];
}

/** The address and read parameters of the Items list's Series menu (decision 7640). */
export const ITEM_SERIES_PARAM = 'series';
export const ITEM_SERIES_MANUFACTURER_PARAM = 'seriesManufacturer';
/**
 * 🔒 The `seriesManufacturer` value naming a series held with no manufacturer (decision 7871), so the
 * menu tells "no manufacturer" from "every manufacturer", which an empty value reads as.
 */
export const ITEM_SERIES_NO_MANUFACTURER = 'none';

/** 🔒 The address parameter the Items list's Filter conditions ride in (decision 8800); the read sends them inside its `criteria`. */
export const LIST_CONDITIONS_PARAM = 'conditions';

/** One entry of the Series menu: a `series` value, the manufacturer holding it, and how many Items carry it. */
export interface ItemSeriesOption {
  series: string;
  manufacturerCompanyId: string | null;
  manufacturerName: string | null;
  count: number;
}

/** A list's segment row: the address parameter, the column a segment's criterion reads, the route its values come from, and the row's name. */
export interface ListSegmentFilter {
  param: string;
  column: string;
  /** Answers `{ rows: { value, label }[] }`; a segment narrows on the label, the value the column holds. */
  optionsPath: string;
  legend: string;
}

export interface ListUrlFilter {
  param: string;
  values: readonly { value: string; title: string }[];
}

/** The value the address names for a list's own filter, or null where it names none or one the list does not take. */
export function listUrlFilterValue(filter: ListUrlFilter | undefined, asked: string | null | undefined): string | null {
  if (!filter || !asked) return null;
  return filter.values.some((value) => value.value === asked) ? asked : null;
}

/** The keys a list page's action row can carry; the label is the string lookup's and the gate is the API's. */
export type ListPageActionKey = 'importVcard' | 'issueStock' | 'returnStock';

export interface ListPageAction {
  key: ListPageActionKey;
}


/**
 * 🔒 Each accounting document list's statuses on the Closed side (decision 2880):
 * the document is fully satisfied, as paid, applied, received or closed,
 * written off, void, canceled, declined or expired. Every other status the
 * document holds is on the Open side. The members are the status table's own,
 * and the register's test refuses one the table does not carry.
 */
export const DOCUMENT_LIST_STATUSES = {
  PurchaseOrder: { entityType: 'PurchaseOrderStatus', closed: ['Received', 'Closed', 'Cancelled'] },
  Quote: { entityType: 'QuoteStatus', closed: ['Accepted', 'Declined', 'Void', 'Expired'] },
  SalesOrder: { entityType: 'SalesOrderStatus', closed: ['Closed', 'Void'] },
  Invoice: { entityType: 'InvoiceStatus', closed: ['Paid', 'WrittenOff', 'Void', 'PartlyWrittenOff'] },
  CreditNote: { entityType: 'CreditNoteStatus', closed: ['Applied', 'Void'] },
  Payment: { entityType: 'PaymentStatus', closed: ['Cleared', 'Void'] },
  Bill: { entityType: 'BillStatus', closed: ['Paid', 'Void'] },
  VendorCredit: { entityType: 'VendorCreditStatus', closed: ['Applied', 'Void'] },
} as const satisfies Partial<Record<PagedRecord, { entityType: string; closed: readonly string[] }>>;

export type DocumentListRecord = keyof typeof DOCUMENT_LIST_STATUSES;

/**
 * 🔒 An accounting document list's built-in views (decision 2880): Open and
 * Closed, starred beside All, then one per status in the table's order and
 * under its label. A status whose label is a combined view's name reads
 * *Closed Only*, so two views in one switcher never share a name.
 */
function documentViews(recordType: DocumentListRecord): BuiltInView[] {
  const { entityType, closed } = DOCUMENT_LIST_STATUSES[recordType];
  const combined = openClosedViews(entityType, closed, { open: t('view.builtIn.open'), closed: t('view.builtIn.closed') });
  const taken = new Set(combined.map((view) => view.name.toLowerCase()));
  return [...combined, ...statusViews(entityType).map((view) => (taken.has(view.name.toLowerCase()) ? { ...view, name: `${view.name} ${t('view.builtIn.only')}` } : view))];
}

export const LIST_PAGES: Readonly<Record<PagedRecord, ListPageSpec>> = {
  CompanyRecord: {
    recordType: 'CompanyRecord',
    label: 'Companies',
    singular: RECORD_KIND_NAMES.CompanyRecord,
    route: RECORD_ROUTES.CompanyRecord.list,
    recordRoute: RECORD_ROUTES.CompanyRecord.record,
    columns: [
      // 🔒 The Marks column first, right after the checkbox (decision 9720; T3164): the gold dot and a missing tax form's triangle.
      { key: 'marks', label: t('listMarks.column'), role: 'secondary', type: 'marks', fit: true },
      { key: 'displayName', label: 'Name', role: 'primary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'CompanyStatus' },
      { key: 'kind', label: 'Kind', role: 'secondary', type: 'pills', fit: true },
      { key: 'paymentTerms', label: 'Terms', role: 'secondary', type: 'text' },
      { key: 'siteCount', label: 'Sites', role: 'value', type: 'number', unit: { one: 'site', other: 'sites' } },
      { key: 'lastContactAt', label: 'Last Contact', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'displayName',
    emptyAction: 'Add the first company with + New Company.',
    create: 'fields',
    /** 🔒 Edit selected changes the account's sales rep (decision 0982). */
    bulkEdit: BULK_EDIT_FIELDS.CompanyRecord,
    /** 🔒 The one list read by kind (T752; decision 1486): each kind a built-in view on the `role` filter the read already takes, all four starred (decision 2880). */
    views: [
      allView(t('company.queue.all')),
      { key: 'customers', name: t('company.queue.customers'), filter: { role: 'customer' }, starred: true },
      { key: 'vendors', name: t('company.queue.vendors'), filter: { role: 'vendor' }, starred: true },
      { key: 'manufacturers', name: t('company.queue.manufacturers'), filter: { role: 'manufacturer' }, starred: true },
      // 🔒 The Marks column's two filters (decision 9720; T3164): a condition on the column's words, unstarred.
      { key: 'missing-w9', name: t('listMarks.missingW9'), criteria: [{ field: 'marks', comparator: 'contains', value: t('listMarks.missingW9') }] },
      { key: 'no-tax-exempt-certificate', name: t('listMarks.noTaxExemptCertificate'), criteria: [{ field: 'marks', comparator: 'contains', value: t('listMarks.noTaxExemptCertificate') }] },
    ],
  },
  PersonRecord: {
    recordType: 'PersonRecord',
    label: 'People',
    singular: RECORD_KIND_NAMES.PersonRecord,
    route: RECORD_ROUTES.PersonRecord.list,
    recordRoute: RECORD_ROUTES.PersonRecord.record,
    columns: [
      // 🔒 The gold dot's own column, first after the checkbox, as on the Companies list (decision 9720; T3164).
      { key: 'marks', label: t('listMarks.column'), role: 'secondary', type: 'marks', fit: true },
      { key: 'fullName', label: 'Name', role: 'primary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'PersonStatus' },
      { key: 'companyName', label: 'Company', role: 'secondary', type: 'text' },
      { key: 'title', label: 'Title', role: 'secondary', type: 'text' },
      { key: 'workEmail', label: 'Work Email', role: 'secondary', type: 'text' },
    ],
    defaultSortKey: 'fullName',
    emptyAction: 'Add the first person with + New Person.',
    // Import vCard reads one or many cards into people, their employments and their contact methods (decision 0341).
    actions: [{ key: 'importVcard' }],
    create: 'fields',
  },
  LocationRecord: {
    recordType: 'LocationRecord',
    label: 'Locations',
    singular: RECORD_KIND_NAMES.LocationRecord,
    route: RECORD_ROUTES.LocationRecord.list,
    recordRoute: RECORD_ROUTES.LocationRecord.record,
    columns: [
      { key: 'addressLine', label: 'Address', role: 'primary', type: 'text' },
      // 🔒 The building's address verification, the one status a Location carries (T77; decision 0639).
      { key: 'verificationStatus', label: 'Status', role: 'status', type: 'status', statusEntity: 'VerificationStatus' },
      { key: 'locality', label: 'City', role: 'secondary', type: 'text' },
      { key: 'administrativeArea', label: 'State', role: 'secondary', type: 'text' },
      { key: 'postalCode', label: 'Postal Code', role: 'secondary', type: 'text' },
      { key: 'siteCount', label: 'Occupants', role: 'value', type: 'number', unit: { one: 'occupant', other: 'occupants' } },
    ],
    defaultSortKey: 'addressLine',
    emptyAction: 'Add the first building with + New Location.',
    create: 'fields',
  },
  SiteRecord: {
    recordType: 'SiteRecord',
    label: 'Sites',
    singular: RECORD_KIND_NAMES.SiteRecord,
    route: RECORD_ROUTES.SiteRecord.list,
    recordRoute: RECORD_ROUTES.SiteRecord.record,
    columns: [
      { key: 'label', label: 'Site', role: 'primary', type: 'text' },
      // 🔒 Decision 10100 (T3476): what kind of place the site is, empty where none is set.
      { key: SITE_TYPE_COLUMN, label: 'Type', role: 'secondary', type: 'text' },
      // 🔒 The site's building's address verification: a Site carries no status of its own, and where it is is what it is (T77; decision 0639).
      { key: 'verificationStatus', label: 'Status', role: 'status', type: 'status', statusEntity: 'VerificationStatus' },
      { key: 'companyName', label: 'Company', role: 'secondary', type: 'text' },
      { key: 'suite', label: 'Suite', role: 'secondary', type: 'text' },
      { key: 'addressLine', label: 'Building', role: 'secondary', type: 'text' },
      { key: 'startDate', label: 'Since', role: 'date', type: 'date' },
    ],
    defaultSortKey: 'label',
    emptyAction: 'Add the first occupancy from a company page.',
    create: 'siteCombo',
    // 🔒 Decision 10100: All, then each active site type, narrowing the read on the Type column.
    segmentFilter: { param: SITE_TYPE_PARAM, column: SITE_TYPE_COLUMN, optionsPath: SITE_TYPE_OPTIONS_PATH, legend: 'Site type' },
  },
  // Piece 17: the catalog list beside the Item page; the columns are declared in piece 13's table.
  Item: {
    recordType: 'Item',
    label: 'Items',
    singular: RECORD_KIND_NAMES.Item,
    route: RECORD_ROUTES.Item.list,
    recordRoute: RECORD_ROUTES.Item.record,
    columns: [
      { key: 'sku', label: 'Part Number', role: 'primary', type: 'text', fit: true },
      // 🔒 The item's picture, off until a view ticks it among its columns (T3333; decision 9380); the id is the Item's `imageFileId`.
      { key: 'imageFileId', label: 'Thumbnail', role: 'secondary', type: 'image', fit: true, offByDefault: true },
      // 🔒 The bare-figure compact label turns the compact line's wrapping on, so a long name shows in full (decisions 1544, 2852).
      { key: 'name', label: 'Name', role: 'secondary', type: 'text', compactLabel: '{value}' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'ItemStatus' },
      { key: 'categoryName', label: 'Category', role: 'secondary', type: 'text' },
      // 🔒 Decision 0900: the tax category beside the catalog one, so a saved view finds the Items a renamed category may hold wrongly.
      { key: 'taxCategoryName', label: 'Tax Category', role: 'secondary', type: 'text' },
      { key: 'unit', label: 'Unit', role: 'secondary', type: 'text' },
      // 🔒 What is on the shelf, summed from the stock ledger and never a stored column (decisions 0153, 0451). A Service has none and draws nothing.
      { key: 'onHand', label: 'On Hand', role: 'value', type: 'number' },
      // 🔒 Decision 3321: what open claims hold of it beside what is on the shelf; on hand less this is what is free.
      { key: 'reserved', label: 'Reserved', role: 'value', type: 'number' },
      { key: 'salesPrice', label: 'Sales Price', role: 'value', type: 'currency' },
    ],
    defaultSortKey: 'sku',
    /** 🔒 An item's name is prose and was cut at about twelve characters in the pane's table (decision 2852). */
    layout: 'compact',
    emptyAction: 'Add the first catalog entry with + New Item.',
    create: 'fields',
    /** 🔒 The row checkboxes Edit selected works over, gated on write on the Item module (piece 17; decisions 0212, 2890, 3620). */
    bulk: 'selectRows',
    /** 🔒 Edit selected changes the fifteen fields of decisions 2890, 7640 and 7790. */
    bulkEdit: BULK_EDIT_FIELDS.Item,
    /** 🔒 The Series menu in the filter row: a product line is ticked in one press (decision 7640; T3125). */
    seriesFilter: true,
    /** 🔒 The Filter button beside it: several conditions at once, attribute values among them (decision 8800; T3276). */
    conditionsFilter: true,
    /** 🔒 The gear leads to Categories, Attribute Types and Pricing Defaults (decision 2805). */
    settingsEntries: PAGE_SETTINGS_ENTRIES.itemList,
    /** 🔒 Off-default accounts is a view, never a toolbar checkbox (decisions 0212, 2880), starred beside All; then one view per Item status. */
    views: [{ key: 'offDefault', name: t('item.list.offDefault'), filter: { offDefaultAccounts: 'true' }, starred: true }, ...statusViews('ItemStatus')],
  },
  // Piece 19: the Deals list beside the Deal page; the board is its second Content shape (decision 0206). A currency column is minor units.
  Deal: {
    recordType: 'Deal',
    label: 'Deals',
    singular: RECORD_KIND_NAMES.Deal,
    route: RECORD_ROUTES.Deal.list,
    recordRoute: RECORD_ROUTES.Deal.record,
    columns: [
      { key: 'name', label: 'Name', role: 'primary', type: 'text' },
      { key: 'customerName', label: 'Account', role: 'secondary', type: 'text' },
      { key: 'stage', label: 'Stage', role: 'status', type: 'status', statusEntity: 'DealStage' },
      { key: 'dealType', label: 'Type', role: 'secondary', type: 'text' },
      { key: 'dealValue', label: 'Value', role: 'value', type: 'currency' },
      { key: 'ownerName', label: 'Owner', role: 'secondary', type: 'text' },
      { key: 'expectedCloseDate', label: 'Expected Close', role: 'date', type: 'date' },
    ],
    defaultSortKey: 'name',
    emptyAction: 'Open the first opportunity with + New Deal.',
    // 🔒 + New opens the New Deal page, never a window (decision 2510).
    create: 'page',
    /** 🔒 The pipeline board is the Deals list's second whole-width rendering (decision 0206). */
    boardable: true,
    /** 🔒 The Tags menu by category, then by tag (T1581, screen E): the Deals list first, on the owner's word of 2026-10-07. */
    tagFilter: true,
  },
  // Piece 22: the Work Orders list beside the Work Order page, newest first; the status column reads the lifecycle axis.
  WorkOrder: {
    recordType: 'WorkOrder',
    label: documentNamePlural('WorkOrder'),
    singular: documentName('WorkOrder'),
    route: RECORD_ROUTES.WorkOrder.list,
    recordRoute: RECORD_ROUTES.WorkOrder.record,
    columns: [
      { key: 'workOrderNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'customerName', label: 'Customer', role: 'secondary', type: 'text' },
      { key: 'siteName', label: 'Site', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'WorkOrderStatus', heldByKey: 'approvalStatus' },
      { key: 'priorityTierName', label: 'Priority', role: 'secondary', type: 'text' },
      { key: 'scheduledStart', label: 'Scheduled', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'workOrderNumber',
    emptyAction: 'Raise the first job with + New Work Order, from here or from a customer’s Work tile.',
    create: 'page',
  },
  // Piece 20: the one Lead queue both feeder paths land in; the held and unrouted queues are this list filtered.
  Lead: {
    recordType: 'Lead',
    label: 'Leads',
    singular: RECORD_KIND_NAMES.Lead,
    route: RECORD_ROUTES.Lead.list,
    recordRoute: RECORD_ROUTES.Lead.record,
    columns: [
      { key: 'name', label: 'Name', role: 'primary', type: 'text' },
      { key: 'companyName', label: 'Company', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'LeadStatus' },
      { key: 'temperature', label: 'Temperature', role: 'secondary', type: 'status', statusEntity: 'LeadTemperature' },
      { key: 'stage', label: 'Stage', role: 'secondary', type: 'status', statusEntity: 'LeadStage' },
      { key: 'source', label: 'Source', role: 'secondary', type: 'text' },
      { key: 'createdAt', label: 'Created', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'createdAt',
    emptyAction: 'Add the first prospect with + New Lead, or let the website form post one.',
    create: 'fields',
    /** 🔒 The one Lead queue and its two filters as built-in views, starred (piece 20; decision 2880); the hygiene screen is a link at the bar's end. */
    views: [
      allView(t('lead.queue.all')),
      { key: 'held', name: t('lead.queue.held'), filter: { status: 'Held' }, starred: true },
      { key: 'unrouted', name: t('lead.queue.unrouted'), filter: { unrouted: 'true' }, starred: true },
    ],
    barLinks: [{ labelKey: 'hygiene.title', route: '/leads/hygiene' }],
  },
  /**
   * 🔒 The requests for quotation, a list of their own under PURCHASING
   * (decision 0488; product owner, 2026-09-17: *"I don't see the RFQ in the
   * tree"*). 💰 An RFQ was reachable only from the Deal that raised it or from
   * a vendor's Work tile, so one raised outside a Deal could be created and
   * never found again.
   */
  RFQ: {
    recordType: 'RFQ',
    label: documentNamePlural('RFQ'),
    singular: documentName('RFQ'),
    route: RECORD_ROUTES.RFQ.list,
    recordRoute: RECORD_ROUTES.RFQ.record,
    columns: [
      { key: 'rfqNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'dealName', label: 'Deal', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'RFQStatus' },
      { key: 'vendorCount', label: 'Vendors', role: 'secondary', type: 'text' },
      { key: 'issuedAt', label: 'Sent', role: 'date', type: 'instant' },
      { key: 'createdAt', label: 'Created', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'createdAt',
    emptyAction: 'A request for quotation is raised on a Deal, from the RFQ frame beside its quote; open a Deal to ask vendors for a price.',
    create: 'none',
  },
  /**
   * 🔒 The bills of materials, a list of their own (decisions 0488, 0565, 1092).
   * Each carries its own name and names its newest attached Deal as a link,
   * counting the rest; a row opens the list's page at its own address. + New
   * makes a list attached to no Deal.
   */
  BOM: {
    recordType: 'BOM',
    label: 'Bills of Materials',
    singular: 'Bill of Materials',
    route: RECORD_ROUTES.BOM.list,
    recordRoute: RECORD_ROUTES.BOM.record,
    columns: [
      { key: 'name', label: 'Name', role: 'primary', type: 'text' },
      // 🔒 A list's status is its own, Draft or Final (decision 1091, superseding 1053).
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'BOMStatus' },
      { key: 'dealName', label: 'Deals', role: 'secondary', type: 'link', linkPattern: '/deals/:dealId' },
      { key: 'customerName', label: 'Account', role: 'secondary', type: 'text' },
      { key: 'lineCount', label: 'Lines', role: 'secondary', type: 'text' },
    ],
    defaultSortKey: 'name',
    emptyAction: 'Add the first bill of materials with + New Bill of Materials, or use Add BOM on a Deal’s own page.',
    create: 'bom',
  },
  // Piece 22: the Purchase Orders list beside the order's page; a currency column is minor units.
  PurchaseOrder: {
    recordType: 'PurchaseOrder',
    label: documentNamePlural('PurchaseOrder'),
    singular: documentName('PurchaseOrder'),
    route: RECORD_ROUTES.PurchaseOrder.list,
    recordRoute: RECORD_ROUTES.PurchaseOrder.record,
    columns: [
      { key: 'purchaseOrderNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'vendorName', label: 'Vendor', role: 'secondary', type: 'text' },
      { key: 'fulfillmentMethod', label: 'Method', role: 'secondary', type: 'text' },
      // 🔒 Decision 1072: the one status the order's gates move, beside the approval that is one of those gates.
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'PurchaseOrderStatus', heldByKey: 'approvalStatus' },
      // 🔒 Decision 1225: a send the host has not confirmed within an hour.
      { key: 'deliveryFlag', label: 'Delivery', role: 'secondary', type: 'status', statusEntity: 'DocumentDeliveryFlag' },
      { key: 'total', label: 'Total', role: 'value', type: 'currency' },
      { key: 'createdAt', label: 'Created', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'createdAt',
    emptyAction: 'Raise the first order with + New Purchase Order, or award a request for quotation.',
    create: 'page',
    /** 🔒 All, Open and Closed starred, then one view per status (decision 2880). */
    views: documentViews('PurchaseOrder'),
  },
  // Piece 22: the Quotes list, the first Document List Page; a currency column is minor units, and the number carries its revision.
  Quote: {
    recordType: 'Quote',
    label: documentNamePlural('Quote'),
    singular: documentName('Quote'),
    route: RECORD_ROUTES.Quote.list,
    recordRoute: RECORD_ROUTES.Quote.record,
    columns: [
      // 🔒 T983: the number takes its own content's width, so the Deal column is not cut while Number has room.
      { key: 'number', label: 'Number', role: 'primary', type: 'text', fit: true },
      { key: 'companyName', label: 'Account', role: 'secondary', type: 'text' },
      // 🔒 A held Draft reads Pending Approval (T1195; decision 1861).
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'QuoteStatus', heldByKey: 'approvalStatus' },
      // 🔒 Decision 1225: a send the host has not confirmed within an hour.
      { key: 'deliveryFlag', label: 'Delivery', role: 'secondary', type: 'status', statusEntity: 'DocumentDeliveryFlag' },
      { key: 'dealName', label: 'Deal', role: 'secondary', type: 'text' },
      { key: 'total', label: 'Total', role: 'value', type: 'currency' },
      { key: 'quoteDate', label: 'Quote Date', role: 'date', type: 'date' },
      { key: 'expiryDate', label: 'Good Till', role: 'date', type: 'date' },
    ],
    defaultSortKey: 'number',
    emptyAction: 'Raise the first quote with + New Quote, from a company or a deal.',
    create: 'page',
    /** 🔒 All, Open and Closed starred, then one view per status (decision 2880). */
    views: documentViews('Quote'),
  },
  // Piece 22, the sales side: the four document lists under ACCOUNTING; a currency column is minor units.
  SalesOrder: {
    recordType: 'SalesOrder',
    label: documentNamePlural('SalesOrder'),
    singular: documentName('SalesOrder'),
    route: RECORD_ROUTES.SalesOrder.list,
    recordRoute: RECORD_ROUTES.SalesOrder.record,
    columns: [
      { key: 'salesOrderNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'companyName', label: 'Account', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'SalesOrderStatus', heldByKey: 'approvalStatus' },
      { key: 'fulfillmentStatus', label: 'Fulfillment', role: 'secondary', type: 'status', statusEntity: 'FulfillmentStatus' },
      // 🔒 Decision 1225: a send the host has not confirmed within an hour.
      { key: 'deliveryFlag', label: 'Delivery', role: 'secondary', type: 'status', statusEntity: 'DocumentDeliveryFlag' },
      { key: 'customerPoNumber', label: 'Customer PO', role: 'secondary', type: 'text' },
      { key: 'total', label: 'Total', role: 'value', type: 'currency' },
      { key: 'createdAt', label: 'Created', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'createdAt',
    emptyAction: 'Convert an accepted quote, or raise an order directly from the account.',
    create: 'page',
    /** 🔒 All, Open and Closed starred, then one view per status (decision 2880). */
    views: documentViews('SalesOrder'),
  },
  Invoice: {
    recordType: 'Invoice',
    label: documentNamePlural('Invoice'),
    singular: documentName('Invoice'),
    route: RECORD_ROUTES.Invoice.list,
    recordRoute: RECORD_ROUTES.Invoice.record,
    columns: [
      { key: 'number', label: 'Number', role: 'primary', type: 'text' },
      { key: 'companyName', label: 'Account', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'InvoiceStatus' },
      { key: 'invoiceDate', label: 'Invoice Date', role: 'date', type: 'date' },
      { key: 'dueDate', label: 'Due', role: 'date', type: 'date' },
      { key: 'total', label: 'Total', role: 'value', type: 'currency' },
      { key: 'amountPaid', label: 'Paid', role: 'value', type: 'currency' },
    ],
    defaultSortKey: 'invoiceDate',
    emptyAction: 'Raise the first invoice from a released sales order or a completed work order.',
    create: 'page',
    /** 🔒 All, Open and Closed starred, then one view per status (decision 2880). */
    views: documentViews('Invoice'),
  },
  CreditNote: {
    recordType: 'CreditNote',
    label: documentNamePlural('CreditNote'),
    singular: documentName('CreditNote'),
    route: RECORD_ROUTES.CreditNote.list,
    recordRoute: RECORD_ROUTES.CreditNote.record,
    columns: [
      { key: 'creditNoteNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'companyName', label: 'Account', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'CreditNoteStatus' },
      { key: 'sourceInvoiceNumber', label: 'Against Invoice', role: 'secondary', type: 'text' },
      { key: 'reason', label: 'Reason', role: 'secondary', type: 'text' },
      { key: 'total', label: 'Credit', role: 'value', type: 'currency' },
      { key: 'creditDate', label: 'Credit Date', role: 'date', type: 'date' },
    ],
    defaultSortKey: 'creditDate',
    emptyAction: 'Raise a credit note from the invoice the customer is disputing or returning against.',
    create: 'page',
    /** 🔒 All, Open and Closed starred, then one view per status (decision 2880). */
    views: documentViews('CreditNote'),
  },
  Payment: {
    recordType: 'Payment',
    label: documentNamePlural('Payment'),
    singular: documentName('Payment'),
    route: RECORD_ROUTES.Payment.list,
    recordRoute: RECORD_ROUTES.Payment.record,
    columns: [
      { key: 'reference', label: 'Reference', role: 'primary', type: 'text' },
      { key: 'companyName', label: 'Account', role: 'secondary', type: 'text' },
      { key: 'direction', label: 'Direction', role: 'secondary', type: 'text' },
      { key: 'method', label: 'Method', role: 'secondary', type: 'text' },
      // 🔒 Decision 0434: where the money moved — the payment's own account when Sent, its Deposit's when Received.
      { key: 'bankAccountName', label: 'Bank Account', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'PaymentStatus' },
      { key: 'amount', label: 'Amount', role: 'value', type: 'currency' },
      { key: 'unallocated', label: 'Unallocated', role: 'value', type: 'currency' },
      { key: 'paymentDate', label: 'Received', role: 'date', type: 'date' },
    ],
    defaultSortKey: 'paymentDate',
    emptyAction: 'Record the first payment against an open invoice.',
    create: 'page',
    // 🔒 Decision 0762 (T123): Payments received and Payments made, the one list filtered by the payment's direction.
    urlFilter: { param: 'direction', values: [{ value: 'Received', title: t('payment.list.received') }, { value: 'Sent', title: t('payment.list.made') }] },
    /** 🔒 All, Open and Closed starred, then one view per status (decision 2880). */
    views: documentViews('Payment'),
  },
  // Piece 22, the purchase and stock side (built 2026-09-15). A currency column is minor units, the List Viewer's rule.
  Bill: {
    recordType: 'Bill',
    label: documentNamePlural('Bill'),
    singular: documentName('Bill'),
    route: RECORD_ROUTES.Bill.list,
    recordRoute: RECORD_ROUTES.Bill.record,
    columns: [
      { key: 'billNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'vendorName', label: 'Vendor', role: 'secondary', type: 'text' },
      { key: 'vendorInvoiceNumber', label: 'Vendor Invoice', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'BillStatus' },
      { key: 'approvalStatus', label: 'Approval', role: 'secondary', type: 'status', statusEntity: 'ApprovalStatus' },
      { key: 'total', label: 'Total', role: 'value', type: 'currency' },
      { key: 'dueDate', label: 'Due', role: 'date', type: 'date' },
    ],
    defaultSortKey: 'dueDate',
    emptyAction: 'Record the first bill with + New Bill, against a purchase order or on its own.',
    create: 'page',
    /** 🔒 All, Open and Closed starred, then one view per status (decision 2880). */
    views: documentViews('Bill'),
  },
  VendorCredit: {
    recordType: 'VendorCredit',
    label: documentNamePlural('VendorCredit'),
    singular: documentName('VendorCredit'),
    route: RECORD_ROUTES.VendorCredit.list,
    recordRoute: RECORD_ROUTES.VendorCredit.record,
    columns: [
      { key: 'creditNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'vendorName', label: 'Vendor', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'VendorCreditStatus' },
      { key: 'approvalStatus', label: 'Approval', role: 'secondary', type: 'status', statusEntity: 'ApprovalStatus' },
      { key: 'amount', label: 'Amount', role: 'value', type: 'currency' },
      { key: 'creditDate', label: 'Credit Date', role: 'date', type: 'date' },
    ],
    defaultSortKey: 'creditDate',
    emptyAction: 'Record the first credit a vendor has granted with + New Vendor Credit.',
    create: 'vendorCredit',
    /** 🔒 All, Open and Closed starred, then one view per status (decision 2880). */
    views: documentViews('VendorCredit'),
  },
  StockCount: {
    recordType: 'StockCount',
    label: documentNamePlural('StockCount'),
    singular: documentName('StockCount'),
    route: RECORD_ROUTES.StockCount.list,
    recordRoute: RECORD_ROUTES.StockCount.record,
    columns: [
      { key: 'countNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'siteName', label: 'Site', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'StockCountStatus' },
      { key: 'scope', label: 'Scope', role: 'secondary', type: 'text' },
      { key: 'lineCount', label: 'Lines', role: 'value', type: 'number' },
      { key: 'startedAt', label: 'Started', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'startedAt',
    emptyAction: 'Count a site’s shelves with + New Stock Count.',
    // 🔒 Issue Stock stands on every stock list (decision 1142), and Return Stock beside it (decision 1609).
    actions: [{ key: 'issueStock' }, { key: 'returnStock' }],
    create: 'page',
  },
  StockTransfer: {
    recordType: 'StockTransfer',
    label: documentNamePlural('StockTransfer'),
    singular: documentName('StockTransfer'),
    route: RECORD_ROUTES.StockTransfer.list,
    recordRoute: RECORD_ROUTES.StockTransfer.record,
    columns: [
      { key: 'transferNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'fromSiteName', label: 'From', role: 'secondary', type: 'text' },
      { key: 'toSiteName', label: 'To', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'StockTransferStatus' },
      { key: 'lineCount', label: 'Lines', role: 'value', type: 'number' },
      { key: 'transferredAt', label: 'Raised', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'transferredAt',
    emptyAction: 'Move stock between two sites with + New Stock Transfer.',
    // 🔒 Issue Stock stands on every stock list (decision 1142), and Return Stock beside it (decision 1609).
    actions: [{ key: 'issueStock' }, { key: 'returnStock' }],
    create: 'page',
  },
  StockRevaluation: {
    recordType: 'StockRevaluation',
    label: documentNamePlural('StockRevaluation'),
    singular: documentName('StockRevaluation'),
    route: RECORD_ROUTES.StockRevaluation.list,
    recordRoute: RECORD_ROUTES.StockRevaluation.record,
    columns: [
      { key: 'revaluationNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'sku', label: 'Item', role: 'secondary', type: 'text' },
      { key: 'siteName', label: 'Site', role: 'secondary', type: 'text' },
      { key: 'quantity', label: 'Quantity', role: 'value', type: 'number' },
      { key: 'oldUnitCost', label: 'Was', role: 'value', type: 'currency' },
      { key: 'newUnitCost', label: 'Now', role: 'value', type: 'currency' },
      { key: 'postedAt', label: 'Posted', role: 'date', type: 'instant' },
    ],
    defaultSortKey: 'postedAt',
    emptyAction: 'Reprice stock on hand with + New Stock Revaluation, stating why.',
    // 🔒 Issue Stock stands on every stock list (decision 1142), and Return Stock beside it (decision 1609).
    actions: [{ key: 'issueStock' }, { key: 'returnStock' }],
    create: 'stockRevaluation',
  },
  /**
   * 🔒 The Manual Journal's list (decision 0761; T139): the same list pane, view
   * picker, paging and + New every accounting document's list carries, beside the
   * entry's own page. Its rows read the date, the number, the total and the status
   * pill, the four the owner's Zoho list draws.
   */
  JournalEntry: {
    recordType: 'JournalEntry',
    label: t('accounting.journal.listTitle'),
    singular: t('accounting.journal.title'),
    route: RECORD_ROUTES.JournalEntry.list,
    recordRoute: RECORD_ROUTES.JournalEntry.record,
    columns: [
      { key: 'entryNumber', label: 'Number', role: 'primary', type: 'text' },
      { key: 'accrualDate', label: 'Date', role: 'date', type: 'date' },
      { key: 'referenceNumber', label: 'Reference Number', role: 'secondary', type: 'text' },
      { key: 'memo', label: 'Notes', role: 'secondary', type: 'text' },
      { key: 'createdByName', label: 'Created By', role: 'secondary', type: 'text' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'JournalEntryStatus' },
      { key: 'total', label: 'Total', role: 'value', type: 'currency' },
    ],
    defaultSortKey: 'accrualDate',
    emptyAction: 'Post the first adjustment with + New Manual Journal.',
    create: 'page',
  },
  /**
   * 🔒 Piece 29, decision 2910: the Service Reports list under OPERATIONS, every
   * visit's report once it opened, with its number, customer, lead, day and state.
   * A row opens the visit's page; nothing is raised from here.
   */
  ServiceReport: {
    recordType: 'ServiceReport',
    label: documentNamePlural('ServiceReport'),
    singular: documentName('ServiceReport'),
    route: RECORD_ROUTES.ServiceReport.list,
    recordRoute: RECORD_ROUTES.ServiceReport.record,
    columns: [
      { key: 'number', label: 'Number', role: 'primary', type: 'text' },
      { key: 'customerName', label: 'Customer', role: 'secondary', type: 'text' },
      { key: 'leadName', label: 'Lead', role: 'secondary', type: 'text' },
      { key: 'scheduledStart', label: 'Date', role: 'date', type: 'instant' },
      { key: 'status', label: 'Status', role: 'status', type: 'status', statusEntity: 'ServiceReportState' },
    ],
    defaultSortKey: 'scheduledStart',
    emptyAction: 'Book a visit on a Work Order’s Visits tile; its first On My Way, Arrived or Clock In opens the report, which reaches the customer signed, beside the job’s invoice.',
    create: 'none',
    /** 🔒 Decision 2910 on 2880: Open, Signed, Sent and Void, one view per state, read off the state's seeded rows. */
    views: statusViews('ServiceReportState'),
  },
};

/**
 * 🔒 Every built-in view a list carries, All first (decision 2880). A list that
 * names its All in its own words declares it; every other list reads All and
 * its label, starred, so its bar stands only once a person stars a second view.
 */
export function builtInViews(spec: ListPageSpec): BuiltInView[] {
  const declared = spec.views ?? [];
  return declared.some((view) => view.key === ALL_VIEW_ID) ? [...declared] : [allView(`All ${spec.label}`), ...declared];
}

/** The record page a row opens, with the id filled in. */
export function recordHref(recordType: PagedRecord, id: string): string {
  return LIST_PAGES[recordType].recordRoute.replace(':id', id);
}

/**
 * 🔒 The kind line a record page draws under its name: the record type's own
 * name, singular (T170). The title bar standard carries exactly one line under
 * the name and it is this one. 💰 It has been three things — a record-kind
 * subtitle, nothing at all, then the headline employment's title and company —
 * and the third is what the product owner asked out again on 2026-09-22.
 */
export function recordKindLine(recordType: PagedRecord): string {
  return LIST_PAGES[recordType].singular;
}

/** The list page a route names, or null where the route is not one of the four. */
export function listPageFor(route: string): ListPageSpec | null {
  return Object.values(LIST_PAGES).find((spec) => spec.route === route) ?? null;
}

/**
 * Global Search's See all N results navigates to that entity type's own List
 * Page, pre-filtered to the term through the same mechanism the New View Modal
 * defines.
 */
export function seeAllHref(recordType: PagedRecord, term: string): string {
  return `${LIST_PAGES[recordType].route}?q=${encodeURIComponent(term)}`;
}
